import React from "react";
import LegalLayout, { Section, Bullets } from "@/components/LegalLayout";

const CONTACT = "privacy@karyowads.com";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="This Privacy Policy explains how KARYOW AI Ads Manager collects, uses, shares, and protects your information when you use our platform at karyowads.com."
      lastUpdated="June 1, 2026"
    >
      <Section title="1. Introduction">
        <p>
          KARYOW AI Ads Manager ("KARYOW", "we", "us", or "our") operates the advertising management
          platform available at karyowads.com (the "Service"). We are committed to protecting your privacy
          and handling your data transparently and lawfully. By using the Service, you agree to the practices
          described in this Privacy Policy.
        </p>
      </Section>

      <Section title="2. Information We Collect">
        <p>We collect the following categories of information:</p>
        <Bullets items={[
          "Account information: your name, email address, profile photo, and workspace/brand details provided during sign-in (including through Google authentication).",
          "Advertising data: campaign, ad set, ad, budget, and performance metrics you create in the platform or that we retrieve from connected Meta (Facebook/Instagram) ad accounts.",
          "Meta integration data: access tokens, ad account identifiers, and insights obtained via the Meta Marketing API when you connect your Meta Business account.",
          "AI content data: prompts, briefs, generated copy, and images you create using the AI Ad Studio.",
          "Usage data: log data, device and browser information, IP address, and interactions with the Service.",
          "Cookies and similar technologies as described in our Cookie Policy.",
        ]} />
      </Section>

      <Section title="3. How We Use Your Information">
        <p>We use your information to:</p>
        <Bullets items={[
          "Provide, operate, maintain, and improve the Service.",
          "Authenticate you and secure your account and workspace.",
          "Connect to and synchronize data with your Meta ad accounts at your request.",
          "Generate AI-powered ad briefs, copy, images, and performance recommendations.",
          "Analyze performance and produce reports, dashboards, and exports.",
          "Communicate with you about updates, security notices, and support.",
          "Detect, prevent, and address fraud, abuse, and technical issues.",
          "Comply with legal obligations.",
        ]} />
      </Section>

      <Section title="4. Legal Bases for Processing">
        <p>
          Where required by law (for example under the GDPR), we process personal data based on: your consent;
          the performance of our contract with you; our legitimate interests in operating and improving the
          Service; and compliance with legal obligations.
        </p>
      </Section>

      <Section title="5. How We Share Information">
        <p>We do not sell your personal data. We may share information with:</p>
        <Bullets items={[
          "Service providers who process data on our behalf (hosting, database, analytics, and AI providers) under confidentiality obligations.",
          "Meta Platforms, when you connect your ad account, to the extent necessary to read and manage your advertising data.",
          "AI processing partners used to generate ad content, limited to the prompts and inputs you submit.",
          "Authorities or third parties where required by law or to protect our rights, users, and the public.",
          "A successor entity in connection with a merger, acquisition, or asset sale.",
        ]} />
      </Section>

      <Section title="6. Data Retention">
        <p>
          We retain your information for as long as your account is active or as needed to provide the Service.
          We will delete or anonymize your personal data upon a valid deletion request, within 30 days, as
          described on our Data Deletion page (karyowads.com/data-deletion), unless we are required to retain it
          for legal, tax, or security reasons.
        </p>
      </Section>

      <Section title="7. Your Rights">
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <Bullets items={[
          "Access the personal data we hold about you.",
          "Request correction of inaccurate or incomplete data.",
          "Request deletion of your data ('right to be forgotten').",
          "Object to or restrict certain processing.",
          "Request a portable copy of your data.",
          "Withdraw consent at any time, without affecting prior processing.",
        ]} />
        <p>
          To exercise these rights, contact us at <span className="text-lime-400">{CONTACT}</span>. You may also
          submit a data deletion request via karyowads.com/data-deletion.
        </p>
      </Section>

      <Section title="8. Meta Platform Data">
        <p>
          When you connect a Meta ad account, we access advertising data solely to provide the Service. We handle
          Meta Platform data in accordance with Meta's Platform Terms and Developer Policies. You can disconnect
          your Meta account at any time from within the platform, and you can request deletion of associated data
          via our Data Deletion page.
        </p>
      </Section>

      <Section title="9. Data Security">
        <p>
          We implement technical and organizational measures to protect your data, including encryption in transit,
          access controls, and secure server-side storage of integration credentials. No method of transmission or
          storage is completely secure, and we cannot guarantee absolute security.
        </p>
      </Section>

      <Section title="10. International Transfers">
        <p>
          Your information may be processed in countries other than your own. Where we transfer personal data
          internationally, we use appropriate safeguards consistent with applicable law.
        </p>
      </Section>

      <Section title="11. Children's Privacy">
        <p>
          The Service is not directed to individuals under 18, and we do not knowingly collect personal data from
          children. If you believe a child has provided us data, please contact us for removal.
        </p>
      </Section>

      <Section title="12. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be posted on this page with a
          revised "Last updated" date. Your continued use of the Service after changes take effect constitutes
          acceptance.
        </p>
      </Section>

      <Section title="13. Contact Us">
        <p>
          For questions about this Privacy Policy or your data, contact us at{" "}
          <span className="text-lime-400">{CONTACT}</span>.
        </p>
      </Section>
    </LegalLayout>
  );
}
